import React, { useState, useEffect, useCallback } from "react";
import { navigate } from "@reach/router";
import styled from "styled-components";
// --- REQUIRED IMPORTS FOR API AND DATA ---
import axios from "axios"; 
import useGame from "../../hooks/useGame"; 
import useAuth from "../../../auth/hooks/useAuth"; 
import useCurrentDuels from "../../hooks/useCurrentDuels"; 
import useUsername from "../../hooks/useUsername"; 
// New component import
import ChallengeCreation from "./ChallengeCreation"; 
import DuelTopicSelection from "./DuelTopicSelection";
import ChallengeResults from "./ChallengeResults";
// -----------------------------------------

import background from "../../assets/background.png";
import DuelAccept from "../../assets/Duels/DuelAccept.png"; 
import DuelIgnore from "../../assets/Duels/DuelIgnore.png"; 


// --- Styled Components ---

export const Buttonz = styled.button`
  background: rgb(234, 146, 19);
  background: linear-gradient(
    0deg,
    rgba(234, 146, 19, 1) 0%,
    rgba(255, 186, 32, 1) 100%
  );
  width: 150px;
  height: 50px;
  border: 2px solid #e88f12;
  color: #fff;
  user-select: none;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  border-radius: 0.25em;
  font-size: 1.5em;
  cursor: pointer;
  z-index: 10;
  position: relative; /* Fixed: Used in HeaderWrapper for correct flow */
`;

export const BodyCode = styled.div`
  width: 100%;
  height: 100vh;
  background-image: url(${background});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px; 
  position: relative;
`;

// NEW: Wrapper for Buttonz and GameTitle to control header layout
const HeaderWrapper = styled.div`
    width: 90%;
    max-width: 1200px;
    display: flex;
    align-items: center;
    /* Space between button and spacer */
    justify-content: space-between; 
    margin-bottom: 30px; 
    position: relative;
`;

export const GameTitle = styled.h1`
    color: #673c8c;
    text-align: center;
    font-size: 4em;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    margin: 0;
    /* FIX: Absolute position the title to center it over the wrapper/content area */
    position: absolute; 
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    z-index: 1; 
`;

const DuelsContentFrame = styled.div`
  position: relative;
  display: flex;
  flex-direction: column; 
  gap: 20px;
  width: 90%;
  max-width: 1200px;
  height: 600px; 
  background: transparent;
  border-radius: 10px;
  padding: 0px 0px 20px 0px; 
`;

const DuelsAndActionsWrapper = styled.div`
  display: flex;
  gap: 20px;
  padding: 0 20px;
  flex-grow: 1;
`;

const DuelsListContainer = styled.div`
  flex: 3;
  background: #ffffff; 
  border: 2px solid #673b8b;
  border-radius: 1em;
  padding: 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  h2 {
    color: #673c8c;
    font-size: 2.5em;
    margin: 0.5em 0;
    text-align: center;
    border-bottom: 2px solid #af9dc4;
  }
`;

const ActionsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  justify-content: center;
  align-items: center; 
`;

const ActionButton = styled.div`
  text-align: center;
  width: 100%;
  max-width: 300px;
  height: 100px;
  line-height: 100px;
  font-size: 2em;
  color: #fff;
  user-select: none;
  background: rgb(234, 146, 19);
  background: linear-gradient(
    0deg,
    rgba(234, 146, 19, 1) 0%,
    rgba(255, 186, 32, 1) 100%
  );
  border: 2px solid #e88f12;
  border-radius: 0.25em;
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.98);
  }
`;

const DisabledActionButton = styled(ActionButton)`
    background: #a0a0a0;
    border: 2px solid #6e6e6e;
    cursor: not-allowed;
    opacity: 0.6;
    &:active {
        transform: none;
    }
`;

const ActionImage = styled.img`
    width: 40px;
    height: 40px;
    cursor: pointer;
    user-select: none;
    transition: transform 0.1s;
    
    &:active {
        transform: scale(0.9);
    }
`;

const DuelItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  margin-bottom: 8px;
  background: #eeeaf5;
  border-radius: 0.5em;
  border: 1px solid #af9dc4;
  font-size: 1.5em;
  color: #673b8c;
`;

export const MessageContainer = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 2em;
  color: #673c8c;
  background: #ffffff;
  border: 2px solid #673b8b;
  border-radius: 1em;
  padding: 20px;
  text-align: center;
`;

// Helper functions 

const getOpponentName = (pupils, currentUserId) => {
    if (!currentUserId || !pupils || pupils.length < 2) {
        if (pupils && pupils.length === 2) {
             return pupils[0].username + " vs " + pupils[1].username;
        }
        return "Unknown Opponent";
    }
    const opponent = pupils.find(p => p.id !== currentUserId);
    return opponent ? opponent.username : "Unknown Opponent";
}

const MockDuelItem = ({ duelData, currentUserId, onActionClick }) => {
    
    let status;
    if (duelData.is_complete) {
        status = "Completed";
    } else if (duelData.pupil_accepted) {
        status = "In Progress"; 
    } else {
        status = "Pending";
    }
    
    if (!duelData.pupils || !currentUserId) return null;

    const opponentUsername = getOpponentName(duelData.pupils, currentUserId);
    
    const currentUserPivot = duelData.pupils.find(p => p.id === currentUserId)?.pivot;
    const currentUserAccepted = currentUserPivot ? currentUserPivot.accepted === 1 : false;

    let showAccept = false;
    let showDecline = false;
    let acceptAction = 'Accept'; 
    let acceptLabel = 'Accept Duel';
    
    const isActionable = !duelData.is_complete && !duelData.is_expired;

    if (isActionable) {
        if (currentUserAccepted) {
            showDecline = true;
            status = "Waiting for Opponent";
        } else {
            showAccept = true;
            showDecline = true;
            status = "Incoming Challenge";
        }
    } else if (duelData.pupil_accepted && !duelData.is_complete) {
        const currentPlayerHasScored = duelData.scores?.some(s => s.pupil_id === currentUserId) ?? false;

        if (!currentPlayerHasScored) {
            showAccept = true; 
            acceptAction = 'Play';
            acceptLabel = 'Play Duel';
        }
        showDecline = true; 
        status = "Ready to Play / Continue";
    }

    return (
        <DuelItem>
            <span>{opponentUsername} - {status}</span>
            <div style={{ display: 'flex', gap: '15px' }}>
                {showAccept && (
                    <ActionImage
                        src={DuelAccept}
                        alt={acceptLabel}
                        onClick={() => onActionClick(duelData, acceptAction)}
                    />
                )}
                {showDecline && (
                    <ActionImage 
                        src={DuelIgnore}
                        alt="Decline/Cancel Duel"
                        onClick={() => onActionClick(duelData, 'Decline')}
                    />
                )}
            </div>
        </DuelItem>
    );
};


// --- Main React Component: DuelsPage ---

const DuelsPage = ({ id }) => { 
  
  const game = useGame(id);
  const { duels, isLoading, error, refetch } = useCurrentDuels(); 
  const { state: authState } = useAuth();
  const usernameDetails = useUsername(); 
  
  const [mode, setMode] = useState('list');
  const [duelToLaunch, setDuelToLaunch] = useState(null);
  
  const currentUserId = usernameDetails.details && usernameDetails.details.pupil_id
      ? usernameDetails.details.pupil_id
      : (authState && authState.user ? authState.user.id : null);
      
  const gameId = parseInt(id, 10);
  const MAX_CHALLENGES = 5;

  // --- Filtering Logic ---
  let relevantActiveDuelsList = [];
  let isChallengeButtonDisabled = true;

  if (duels) {
      const allRelevantActiveDuels = duels
          .filter(d => 
              !d.is_complete && 
              d.galaxy_id === gameId
          )
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      const activeDuelCount = allRelevantActiveDuels.length;
      
      isChallengeButtonDisabled = activeDuelCount >= MAX_CHALLENGES;
      relevantActiveDuelsList = allRelevantActiveDuels.slice(0, MAX_CHALLENGES);
  }
  
  // --- Handler Functions ---

  const handleBack = () => {
    if (mode === 'challenge' || mode === 'launchGame' || mode === 'results') {
        setMode('list');
        setDuelToLaunch(null); // IMPORTANT: Reset launch data
        refetch(); // IMPORTANT: Refetch to update the duel list status
    } else {
        navigate("."); 
    }
  };

  const acceptDuel = useCallback(async (duelData) => {
        const duelId = duelData.id;
        const pupilId = currentUserId; // Assuming currentUserId is pupil_id here
        
        if (!duelId || !pupilId || !authState?.access_token) {
            console.error("Missing duel ID, pupil ID, or auth token for acceptance.");
            alert("Error: Missing user or duel information.");
            return;
        }

        const API_URL = `${process.env.REACT_APP_API}/duels/accept`;
        
        // Your API may use FormData or a JSON body. I'll use JSON body for consistency
        // with your decline logic, but with a different URL/API endpoint structure.
        
        try {
            console.log(`Attempting to accept Duel ID: ${duelId}`);
            
            const response = await axios.post(
                API_URL, 
                { // Request Body (assuming JSON is accepted)
                    duel_id: duelId,
                    pupil_id: pupilId,
                },
                { 
                    headers: {
                        Authorization: `Bearer ${authState.access_token}`,
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                }
            );

            const acceptedDuelData = response.data;
            console.log(`DUEL ACCEPTANCE SUCCESS. Duel ID: ${acceptedDuelData.id}. Launching game.`);
            
            // Set state to trigger game launch via DuelTopicSelection
            setDuelToLaunch(acceptedDuelData);
            setMode('launchGame'); // Switch to the new launch mode

        } catch (error) {
            console.error("DUEL ACCEPTANCE FAILED", error.response ? error.response.data : error.message);
            const errorMessage = error.response 
                ? `Duel Acceptance Failed. Status: ${error.response.status}. Message: ${error.response.data.message || 'Server Error'}`
                : `Duel Acceptance Failed. Network Error: ${error.message}`;
            alert(errorMessage); 
            refetch(); 
        }
    }, [authState, currentUserId, refetch]);


  const handleIssueChallenge = () => {
    if (!isChallengeButtonDisabled) {
        setMode('challenge'); 
    }
  };

  const handleChallengeResults = () => {
        setMode('results');
    };

  const handleDuelActionOld = async (duelId, action) => {
    console.log(`Action: ${action} on duel ID: ${duelId}`);
    
    
    if (action === 'Decline') {
        if (!currentUserId || !authState.access_token) {
            console.error("Cannot decline duel: Authentication details are missing.");
            alert("Critical Authentication Error: Cannot process duel action.");
            return;
        }

        try {
            const declineUrl = `${process.env.REACT_APP_OAUTH_URL}/api/duels/decline`;
            
            const response = await axios.post(
                declineUrl, 
                { // Request Body
                    duel_id: duelId,
                    pupil_id: currentUserId,
                },
                { // **THIS CONFIGURATION OBJECT IS THE FIX: Headers for Authorization**
                    headers: {
                        'Authorization': `Bearer ${authState.access_token}`,
                        'Accept': 'application/json' 
                    }
                }
            );
            
            console.log(response.data.message);
            
            // Re-fetch the duels list to update the UI
            refetch(); 

        } catch (error) {
            console.error("Error declining duel:", error);
            const errorMessage = error.response?.data?.message || "Failed to decline duel. Check console for details.";
            alert(errorMessage);
        }
    }
}

const handleDuelAction = async (duelData, action) => {
    // 💡 NEW LINE: Extract the duelId from the data object
    const duelId = duelData.id; 

    console.log(`Action: ${action} on duel ID: ${duelId}`);
    
    if (action === 'Accept') {
        // NEW LOGIC: Call the acceptance function with the full data
        await acceptDuel(duelData);
        return; // Stop execution after launching or failing acceptance
    }
    
    if (action === 'Play') {
        // NEW LOGIC: Launch the game directly
        setDuelToLaunch(duelData);
        setMode('launchGame');
        return;
    }

    // Existing Decline Logic (Now safely uses the extracted duelId)
    if (action === 'Decline') {
        if (!currentUserId || !authState.access_token) {
            console.error("Cannot decline duel: Authentication details are missing.");
            alert("Critical Authentication Error: Cannot process duel action.");
            return;
        }

        try {
            const declineUrl = `${process.env.REACT_APP_OAUTH_URL}/api/duels/decline`;
            
            const response = await axios.post(
                declineUrl, 
                { // Request Body
                    duel_id: duelId, 
                    pupil_id: currentUserId,
                },
                { // **THIS CONFIGURATION OBJECT IS THE FIX: Headers for Authorization**
                    headers: {
                        'Authorization': `Bearer ${authState.access_token}`,
                        'Accept': 'application/json' 
                    }
                }
            );
            
            console.log(response.data.message);
            refetch(); 

        } catch (error) {
            console.error("Error declining duel:", error);
            const errorMessage = error.response?.data?.message || "Failed to decline duel. Check console for details.";
            alert(errorMessage);
        }
    }
}


    const handleFinalGameExit = () => {
        setMode("");
        handleBack(); 
        refetch();
    };


    if (mode === 'results' && game) {
    // Pass the duels hook result object and the back function
    return (
        <ChallengeResults 
            game={game} 
            onBack={handleBack} 
            data={{ 
                duels: duels, 
                currentUserId: currentUserId,
            }}
        />
    );
    }


    if (mode === 'launchGame' && duelToLaunch && game) {
        return (
            <DuelTopicSelection
                game={game}
                // selectedPupil is only needed for the creation flow, safe to pass null here.
                selectedPupil={null} 
                // handleBack will be called when the user exits the game within DuelTopicSelection
                onBack={handleFinalGameExit} 
                // Calling refetch on final exit ensures the duels list is updated.
                exitGame={handleFinalGameExit} 
                pupil_id={currentUserId}
                // CRUCIAL PROP: DuelTopicSelection uses this to skip topic selection and launch the game immediately.
                initialDuelData={duelToLaunch} 
            />
        );
    }
  // --- Conditional Rendering for Challenge Creation Screen ---
  if (mode === 'challenge') {
      return (
          <BodyCode>
              <HeaderWrapper>
                <Buttonz onClick={() => setMode('list')}>Back</Buttonz>
                <GameTitle>{game.description} Challenge</GameTitle>
                {/* Spacer element to push the title to the center */}
                <div style={{ width: '120px', height: '40px' }} /> 
              </HeaderWrapper>
              <ChallengeCreation 
                  game={game} 
                  currentUserId={currentUserId} 
                  onBack={handleBack}
              />
          </BodyCode>
      );
  }

  // --- Duels List Loading / Error State Check ---
  if (!game || isLoading || error) {
    let message = "Loading...";
    let messageColor = '#673c8c';

    if (!game) {
        message = "Loading Game Data...";
    } else if (error) {
        message = `API Error: ${error}`;
        messageColor = 'red';
    } else if (isLoading) {
        message = "Loading Active Duels...";
    }

    const ChallengeButton = isChallengeButtonDisabled ? DisabledActionButton : ActionButton;

    return (
        <BodyCode>
            <HeaderWrapper>
                <Buttonz onClick={handleBack}>Back</Buttonz>
                <GameTitle>{game ? game.description : ''} Duels</GameTitle>
                <div style={{ width: '120px', height: '40px' }} /> 
            </HeaderWrapper>
            <DuelsContentFrame>
                <DuelsAndActionsWrapper>
                    <MessageContainer style={{color: messageColor}}>
                        {message}
                    </MessageContainer>
                    <ActionsContainer>
                        <ChallengeButton onClick={handleIssueChallenge}>Issue Challenge</ChallengeButton>
                        <ActionButton onClick={handleChallengeResults}>Challenge Results</ActionButton>
                    </ActionsContainer>
                </DuelsAndActionsWrapper>
            </DuelsContentFrame>
        </BodyCode>
    );
  }

  // --- Duels List Normal Render UI ---
  const authWarningMessage = (!currentUserId) ? (
      <p style={{ color: 'red', textAlign: 'center', padding: '10px', border: '1px solid red', borderRadius: '5px', marginTop: '10px', fontSize: '1.2em' }}>
          **Critical Authentication Warning:** Cannot identify logged-in user. Duel actions may fail.
      </p>
  ) : null;
   
   const ChallengeButtonComponent = isChallengeButtonDisabled ? DisabledActionButton : ActionButton;

   return (
        <BodyCode>
            <HeaderWrapper>
                <Buttonz onClick={handleBack}>Back</Buttonz>
                <GameTitle>{game.description} Duels</GameTitle>
                <div style={{ width: '120px', height: '40px' }} /> 
            </HeaderWrapper>
            <DuelsContentFrame>
                <DuelsAndActionsWrapper>
                    <DuelsListContainer>
                        <h2>Incoming Duels / Active Challenges</h2>
                        
                        {authWarningMessage}

                        {relevantActiveDuelsList.length === 0 && (
                            <p style={{ textAlign: 'center', marginTop: '50px', fontSize: '1.5em', color: '#af9dc4' }}>
                                No active challenges. Issue a new one!
                            </p>
                        )}

                        {
                            relevantActiveDuelsList.map(duel => (
                                <MockDuelItem 
                                    key={duel.id} 
                                    duelData={duel} 
                                    currentUserId={currentUserId}
                                    onActionClick={handleDuelAction}
                                />
                            ))
                        }
                    </DuelsListContainer>

                    <ActionsContainer>
                        <ChallengeButtonComponent onClick={handleIssueChallenge}>Issue Challenge</ChallengeButtonComponent>
                        <ActionButton onClick={handleChallengeResults}>Challenge Results</ActionButton>
                    </ActionsContainer>
                </DuelsAndActionsWrapper>
              
            </DuelsContentFrame>
        </BodyCode>
      )
};

export default DuelsPage;